import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import HttpClient from "@/api/HttpClient";
import { APIHosts, ApiReduxHelpers } from "@/services/reduxHelpers";

/**
 * Drive Service
 * Handles Company Drive and User Drive operations
 */

export interface CompanyDriveResponse {
  id: string;
  companyId: string;
  driveName: string;
  type: "company" | "user";
  storageSize: number;
  capacitySize: number;
  totalDocuments: number;
  totalSubFolders: number;
  totalUsers: number;
  parentId: string;
  externalId: string;
  labelId: string; // This is the companyRootId
  storageSizeGB: number;
  capacitySizeGB: number;
  remainingBytes: number;
  usagePercentage: number;
  isOverQuota: boolean;
  storageSizeFormatted: string;
  capacitySizeFormatted: string;
  remainingFormatted: string;
  createdDate: string;
  lastUpdated: string;
  lastRecalculatedAt: string | null;
  isActive: boolean;
}

class DriveServiceController extends ApiReduxHelpers {
  constructor() {
    super();
  }

  ApiHost = APIHosts.Upload;

  /**
   * Check if company root exists
   * GET /Drive/company/{companyId}/type/company/org/{orgId}
   */
  public checkCompanyRoot = (
    companyId: string,
    orgId: string
  ): Observable<CompanyDriveResponse[]> => {
    const url = `${this.getHost(this.ApiHost)}/Drive/company/${companyId}/type/company/org/${orgId}`;
    return HttpClient.get<CompanyDriveResponse[]>(url).pipe(map((res) => res));
  };

  /**
   * Create company root folder
   * POST /Drive/createFolderRootProject
   * Body: { name: "root{orgId}", type: "folder", createUser: { id, name, icon, email } }
   */
  public createCompanyRoot = (
    orgId: string,
    companyId: string,
    data: {
      name: string;
      type: string;
      createUser: {
        id: string;
        name: string;
        icon: string;
        email: string;
      };
    }
  ): Observable<any> => {
    const params = new URLSearchParams({ projectId: orgId, companyId });
    const url = `${this.getHost(this.ApiHost)}/Label/createFolderRootProject?${params.toString()}`;
    return HttpClient.post(url, data).pipe(map((res) => res));
  };

  /**
   * Check if user root exists
   * GET /Document/getFolderRootId/{projectId}
   */
  public checkUserRoot = (userId: string): Observable<string> => {
    const url = `${this.getHost(this.ApiHost)}/Document/getFolderRootId/${userId}`;
    return HttpClient.get<string>(url).pipe(map((res) => res));
  };

  /**
   * Create user root folder with parentId
   * POST /Label/createFolderRootProject?projectId={userId}&companyId={companyId}
   * Body: { name: "root{userId}", type: "folder", parentId: companyRootId }
   */
  public createUserRoot = (
    userId: string,
    companyId: string,
    data: {
      name: string;
      type: string;
      parentId: string;
    }
  ): Observable<any> => {
    const params = new URLSearchParams({ projectId: userId, companyId });
    const url = `${this.getHost(this.ApiHost)}/Label/createFolderRootProject?${params.toString()}`;
    return HttpClient.post(url, data).pipe(map((res) => res));
  };

  /**
   * Get files by folder root
   * GET /Document/fileRoots/{rootFolderId}
   */
  public getFilesByFolderRoot = (
    rootFolderId: string,
    params?: Record<string, any>
  ): Observable<any> => {
    const queryString = params
      ? "?" + new URLSearchParams(params as Record<string, string>).toString()
      : "";
    const url = `${this.getHost(this.ApiHost)}/Document/fileRoots/${rootFolderId}${queryString}`;
    return HttpClient.get(url).pipe(map((res) => res));
  };
}

export const DriveService = new DriveServiceController();
